<template>
  <teleport to="body">
    <div v-if="visible" class="modal-overlay">
      <div class="modal-container">
        <!-- 1) Header fixo -->
        <div class="modal-header">
          <h6>{{ modalTitle }}</h6>
          <i class="bi bi-x-circle close-icon" @click="close" />
        </div>

        <!-- 2) Body scrollable -->
        <div class="modal-body">
          <!-- Loading ou erro -->
          <div v-if="isLoading || previewLoading || erro || previewError" class="d-flex flex-column align-items-center p-4">
            <LoadingSpinner />
            <p v-if="erro" class="alert alert-danger mt-3 text-center">{{ erro }}</p>
            <p v-if="previewError" class="alert alert-warning mt-3 text-center">{{ previewError }}</p>
          </div>

          <!-- Conteúdo do certificado -->
          <div v-else>

            <!-- Cabeçalho do e-mail -->
            <div class="mb-2">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <strong>Para:</strong>

                <!-- ✅ ReadOnly: permitir corrigir e-mail do destinatário para reenviar -->
                <template v-if="readOnly">
                  <input
                    v-model="responsavelEmail"
                    type="email"
                    class="form-control form-control-sm"
                    style="max-width: 420px;"
                    placeholder="email@exemplo.com"
                    :disabled="isSending || previewLoading || isLoading"
                  />

                  <span v-if="!emailValidoReadOnly && responsavelEmail?.trim()" class="text-danger small">
                    E-mail inválido
                  </span>

                  <span v-else class="text-muted small">
                    (altere se necessário e clique em Reenviar)
                  </span>
                </template>

                <!-- Normal: exibição padrão -->
                <template v-else>
                  <span>{{ displayTo }}</span>
                </template>
              </div>

              <div class="d-flex flex-wrap align-items-center gap-2 mt-1">
                <strong>Assunto:</strong>
                <span>{{ modalTitle }}</span>
              </div>
            </div>

            <!-- Pré-visualização do e-mail -->
            <div class="mb-3">
              <div class="email-preview border rounded p-3" v-safe-html="emailHtmlExibicao"></div>
            </div>

            <div v-if="resumoAvaliacao" class="row g-2 mb-3 small text-muted">
              <div class="col-12"><strong>Resumo da Avaliação</strong></div>
              <div class="col-md-4">Data: {{ globalUtils.formatarData(resumoAvaliacao.data_realizacao) }}</div>
              <!-- <div class="col-md-4">Animal: {{ resumoAvaliacao.animal_nome }}</div>
              <div class="col-md-4">Formulário: {{ resumoAvaliacao.formulario_nome }}</div> -->
              <div class="col-md-4">Nota: {{ resumoAvaliacao.nota.toFixed(2).replace('.', ',') }}</div>
              <div class="col-md-4">
                Status:
                <span :class="resumoAvaliacao.aprovado ? 'text-success' : 'text-danger'">
                  {{ resumoAvaliacao.aprovado ? 'Aprovado' : 'Reprovado' }}
                </span>
              </div>
            </div>

            <!-- Pré-visualização -->
            <div class="mb-2 d-flex justify-content-between align-items-center">
              <h6 class="m-0">Pré-visualização do Certificado</h6>
              <button class="btn btn-outline-secondary btn-sm" @click="gerarPrevia" :disabled="isGeneratingPreview">
                <span v-if="isGeneratingPreview">Gerando…</span>
                <span v-else>Gerar Prévia</span>
              </button>
            </div>
            <canvas ref="canvasPreview" class="border rounded w-100" style="max-height: 520px; background:#f8f9fa;"></canvas>

            <!-- Aviso de leitura / auditoria -->
            <div
              v-if="readOnly"
              class="mt-3 text-muted fst-italic text-end"
              style="font-size: 0.7rem;"
            >
              E-mail enviado em {{ enviadoEm }} por {{ enviadoPor }}
            </div>
          </div>
        </div>

        <!-- 3) Footer fixo -->
        <div class="modal-footer">
          <template v-if="readOnly">
            <b-button variant="secondary" @click="close">
              Fechar
            </b-button>

            <b-button
              variant="primary"
              :disabled="isSending || isGeneratingPreview || !emailValidoReadOnly"
              @click="reenviarCertificado"
            >
              <span v-if="isSending">Reenviando…</span>
              <span v-else>Reenviar</span>
            </b-button>
          </template>

          <template v-else>
            <b-button variant="secondary" @click="close">Cancelar</b-button>
            <b-button
              variant="primary"
              :disabled="isSending || isGeneratingPreview || !alunoNome || !nivelTexto || !emailValido || !emailTo"
              @click="enviarCertificado"
            >
              <span v-if="isSending">Enviando…</span>
              <span v-else>Enviar E-mail</span>
            </b-button>
          </template>
        </div>

      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance } from 'vue';
import axios from '@/services/axios';
import { BButton } from 'bootstrap-vue-next';
import globalUtils from '@/utils/globalUtils.js';
// import LoadingSpinner from '@/components/Layout/LoadingSpinner.vue';

// Localidade vinda da empresa (sem UI)
const estados      = ref([]);   // [{ id: cod, nome, sigla }]
const municipios   = ref([]);   // [{ id: cod_ibge, nome }]
const estadoCod    = ref(null); // empresa.estado_cod
const municipioId  = ref(null); // empresa.municipio_cod
const sentEmail = ref(null);   // snapshot do envio (db)
const sentHtml  = ref('');     // corpo_html enviado
const alunoIdEfetivo = ref(null);

const estadoSigla = computed(() => {
  const e = estados.value.find(x => Number(x.id) === Number(estadoCod.value));
  return e?.sigla || '';
});
const municipioNome = computed(() => {
  const m = municipios.value.find(x => Number(x.id) === Number(municipioId.value));
  return m?.nome || '';
});

const emailHtmlExibicao = computed(() => {
  // Se houver preview atual da supervisão, mostra o conteúdo atualizado
  if (corpoHtml.value) return corpoHtml.value;

  // Fallback: mostra o snapshot antigo apenas se ainda não carregou preview atual
  if (props.readOnly) return sentHtml.value || '';

  return '';
});

const dataCertificadoEfetiva = computed(() => {
  // readOnly → usa a data do envio real
  if (props.readOnly && sentEmail.value?.enviado_em) {
    return sentEmail.value.enviado_em;
  }

  // fallback: data de emissão atual
  return dataEmissao.value;
});

const { appContext } = getCurrentInstance();
const userName = appContext?.config?.globalProperties?.$globalState?.userName || '';

const resumoAvaliacao = ref(null);

const props = defineProps({
  visible:   { type: Boolean, default: false },
  readOnly:  { type: Boolean, default: false },
  aluno:     { type: Object,  required: true }, // { id, nome, nivel_texto?, responsavel_email?, enviado_em?, por? }
  empresaId: { type: Number,  default: 1 },
  supervisaoId: { type: [Number, String], default: null },
});

console.log("Props: ", props)

const emit = defineEmits(['close', 'sent']);

// Estados
const isLoading = ref(false);
const isSending = ref(false);
const isGeneratingPreview = ref(false);
const erro = ref('');

// Empresa / layout / imagem
const empresa = ref({});
const layout = ref(null);
const certificadoImgUrl = ref(''); // endpoint público (sem token) para a imagem base
const canvasPreview = ref(null);

// Campos do formulário
const alunoNome = computed(() => props.aluno?.nome || '');
const nivelTexto = ref(props.aluno?.nivel_texto || '');
const dataEmissao = ref(
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10)
); // YYYY-MM-DD (LOCAL)
const responsavelEmail = ref(props.aluno?.responsavel_email || '');

// Nível e cor
const nivelId = ref(null);
const corBorda = ref(null); // hex do nível (ex.: #0E2C83)
const corDefault = computed(() => layout.value?.borda?.defaultHex || '#333333');
const dpiCert = computed(() => Number(empresa.value?.certificado_dpi) || 300);
const ptToPx = (pt) => (pt * dpiCert.value) / 72;

const emailValido = computed(() => {
  const e = (responsavelEmail.value || '').trim()
  // regra simples, suficiente pro form:
  return !!e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
})

// Auditoria (somente readOnly)
const enviadoPor = computed(() =>
  (sentEmail.value?.por) || props.aluno?.por || ''
);

const enviadoEm = computed(() => {
  const dt = sentEmail.value?.enviado_em || props.aluno?.enviado_em;
  return dt ? globalUtils.formatarData(dt) : '';
});

// (no <script setup>)
const preview = ref(null);
const previewLoading = ref(false);
const previewError = ref(null);

const emailTo = computed(() => preview.value?.responsavel_email || '');
const displayTo = computed(() => {
  const nome = preview.value?.responsavel_nome || '';
  const mail = preview.value?.responsavel_email || '';
  return (nome && mail) ? `${nome} <${mail}>` : (mail || nome || '—');
});

// Título/Assunto do modal = Nome do formulário – Nome do aluno – dataEmissao
const modalTitle = computed(() => {
  const form =
    preview.value?.formulario_nome ||
    resumoAvaliacao.value?.formulario_nome ||
    '';

  const aluno = alunoNome.value || '';

  if (form && aluno) {
    return `${form} – ${aluno}`;
  }

  if (props.readOnly && sentEmail.value?.assunto) {
    return sentEmail.value.assunto;
  }

  return `Troca de Nível – ${aluno}`;
});

// Escapar HTML
function esc(s) {
  return String(s ?? '')
    .replace(/&/g,'&amp;').replace(/</g,'&lt;')
    .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
}

// Formata valores numéricos como no PDF (8 -> 8,00)
function fmtNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : String(v ?? '—');
}

// Normaliza a resposta vinda do preview (string | array | object)
function formatResp(res) {
  if (res == null) return '—';

  // string já pronta
  if (typeof res === 'string') return res;

  // array (ex.: múltipla seleção já resolvida)
  if (Array.isArray(res)) {
    return res.map((x) => String(x)).join(', ');
  }

  // objeto (ex.: { "Andar nos estribos": 8, "Postura do tronco": 7 })
  if (typeof res === 'object') {
    const parts = [];
    for (const [k, v] of Object.entries(res)) {
      parts.push(`${k}: ${fmtNum(v)}`);
    }
    return parts.length ? parts.join(' • ') : '—';
  }

  // fallback
  return String(res);
}

async function loadSentEmail() {
  if (!props.supervisaoId) {
    sentEmail.value = null;
    sentHtml.value = '';
    preview.value = null;
    previewError.value = 'supervisaoId ausente.';
    return;
  }

  if (sentEmail.value?.enviado_em) {
    dataEmissao.value = sentEmail.value.enviado_em.slice(0, 10);
  }

  previewLoading.value = true;
  previewError.value = null;

  try {
    const { data } = await axios.get(`/operacional/supervisoes/${props.supervisaoId}/email-avaliacao`);

    alunoIdEfetivo.value = data?.supervisao?.aluno_id || props.aluno?.id || null;

    // Esperado: { supervisao: {...}, email: {...} }
    const em = data?.email || null;
    sentEmail.value = em;
    sentHtml.value = em?.corpo_html || '';

    // Preenche preview com o mínimo para o cabeçalho do modal (Para/Assunto)
    preview.value = {
      responsavel_nome:  em?.destinatario_nome || '',
      responsavel_email: em?.destinatario_email || '',
      formulario_nome:   'Troca de Nível' // opcional; pode deixar vazio
    };

    // também reflete no campo de email (só pra exibição)
    if (em?.destinatario_email) responsavelEmail.value = em.destinatario_email;

  } catch (e) {
    console.error('loadSentEmail erro:', e);
    previewError.value = 'Falha ao carregar e-mail enviado.';
    sentEmail.value = null;
    sentHtml.value = '';
    preview.value = null;
  } finally {
    previewLoading.value = false;
  }
}

// Vars que o cabeçalho/rodapé esperam e de onde vêm
const docVars = computed(() => {
  // fontes
  const aluno            = alunoNome.value || '';
  const avaliacao        = preview.value?.formulario_nome || (resumoAvaliacao.value?.formulario_nome || '');
  const dataReal         = preview.value?.data_realizacao || resumoAvaliacao.value?.data_realizacao || '';
  const dataRealFmt      = globalUtils?.formatarData ? globalUtils.formatarData(dataReal) : (dataReal || '');
  const instrutor        = preview.value?.instrutor_nome || '';
  const responsavelNome  = preview.value?.responsavel_nome || '';
// Usa telefone1 ou telefone2 (qual existir primeiro)
  const telefoneEmpresa =
    empresa.value?.telefone1 ||
    empresa.value?.telefone2 ||
    '';
  const nomeEmpresa      = empresa.value?.nome_fantasia || '';
  const emailEmpresa      = empresa.value?.email || '';

  return {
    responsavel_nome: responsavelNome,
    aluno: aluno,
    avaliacao: avaliacao,
    data_realizacao: dataRealFmt,          // usei dd/mm/aaaa como no exemplo
    instrutor_nome: instrutor,
    telefone: telefoneEmpresa,
    nome_empresa: nomeEmpresa,
    email: emailEmpresa,
  };
});

// Renderiza {{chaves}} com escape (para não quebrar HTML)
function renderDoc(html, vars) {
  const h = String(html || '');
  return h.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key) => {
    const val = (vars && key in vars) ? vars[key] : '';
    return esc(val);
  });
}


const corpoHtml = computed(() => {
  if (!preview.value) return '';

  const vars = docVars.value;

  // 1) Renderiza Documentos com {{variáveis}}
  const cab = renderDoc(preview.value.cabecalho_html || '', vars);
  const rod = renderDoc(preview.value.rodape_html    || '', vars);

  // 2) Bloco do formulário + Q&A
  const form = String(preview.value.formulario_nome || (resumoAvaliacao.value?.formulario_nome || ''));
  const itens = (preview.value.resumo || [])
    .map(p => {
      const pergunta = esc(p?.pergunta);
      const resposta = esc(formatResp(p?.resposta));
      return `<li style="margin-bottom:4px;"><strong>${pergunta}:</strong> ${resposta}</li>`;
    })
    .join('');

  const bloco = `
    <div style="margin-top:8px;margin-bottom:4px;font-weight:600;">${esc(form)}</div>
    <div style="margin-bottom:4px;">Perguntas e respostas:</div>
    <ul style="list-style:none;padding-left:0;margin:0;">${itens}</ul>
  `;

  // 3) Montagem final
  return `${cab}${bloco}${rod}`;
});


// Helpers
function apiBase() {
  const base = (axios?.defaults?.baseURL || '').replace(/\/+$/, '');
  return base;
}

async function carregarEstados() {
  const { data } = await axios.get('/localidades/estados');
  estados.value = data || [];
}

async function carregarMunicipiosDoEstado(cod) {
  municipios.value = [];
  if (!cod) return;
  const { data } = await axios.get(`/localidades/municipios/${cod}`);
  municipios.value = data || [];
}

async function carregarLocalidadePorEmpresa() {
  // Requer empresa.value preenchida
  const est = empresa.value?.estado_cod;
  const mun = empresa.value?.municipio_cod;
  if (!est || !mun) return;

  await carregarEstados();
  estadoCod.value = Number(est);
  await carregarMunicipiosDoEstado(estadoCod.value);
  municipioId.value = Number(mun);
}

async function carregarResumoSupervisao() {
  if (!props.supervisaoId) { resumoAvaliacao.value = null; return; }
  try {
    const { data } = await axios.get(`/operacional/alunos-supervisoes/${props.supervisaoId}`);
    // Ajuste os nomes abaixo conforme as colunas que sua query retornar:
    resumoAvaliacao.value = {
      data_realizacao: data.data_realizacao,
      animal_nome:     data.animal_nome || data.animal || '—',
      formulario_nome: data.formulario_nome || data.formulario || '—',
      nota:            Number(data.nota_avaliacao ?? data.nota ?? 0),
      aprovado:        !!(data.aprovado ?? (data.nota_avaliacao >= 7))
    };
  } catch (_) {
    resumoAvaliacao.value = null;
  }
}


async function safeGerarPrevia() {
  try {
    await gerarPrevia();
  } catch (err) {
    // Prévia é opcional: não bloqueia o fluxo
    // Em dev, avisa; em produção, fica silencioso
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[EmailTrocaNivel] Falha ao gerar prévia:', err);
    }
  }
}


async function carregarNivelDoAluno() {
  const alunoId = alunoIdEfetivo.value || props.aluno?.id;
  if (!alunoId) return;

  try {
    const { data } = await axios.get(
      `/configuracoes/certificados/aluno/${alunoId}/nivel`,
      { params: { supervisaoId: props.supervisaoId || undefined } }
    );
    // data = { matricula_id, nivel_id, nivel_nome }
    nivelTexto.value = data?.nivel_nome || '';
    nivelId.value = data?.nivel_id || null;
    // Busca cor do nível (o seu GET /cadastros/niveis-turmas/:id já retorna cor_borda)
    if (nivelId.value) {
      const r2 = await axios.get(`/cadastros/niveis-turmas/${nivelId.value}`);
      corBorda.value = r2?.data?.cor_borda || null;
    } else {
      corBorda.value = null;
    }
  } catch (e) {
    console.warn('Não foi possível obter o nível do aluno:', e?.response?.data || e.message);
    nivelId.value = null;
    corBorda.value = null;
    // Mantém vazio para permitir digitação manual, se necessário
  }
  // Gera/atualiza prévia após definir cor e nível
  await safeGerarPrevia();
}

async function carregarEmpresaELayout() {
  const res = await axios.get(`/configuracoes/empresa`);
  // console.log('[empresa]', res.data);
  empresa.value = res.data || {};
  layout.value  = empresa.value.certificado_layout || null;

  // Monta URL pública da imagem do certificado (sem token)
  certificadoImgUrl.value = `${apiBase()}/configuracoes/empresa/certificado-modelo?v=${Date.now()}`;

  if (!layout.value) {
    throw new Error('Layout do certificado não configurado para a escola.');
  }
}

// Formata a data "31 de outubro de 2025" (usa util se existir; senão faz fallback)
function formatarDataExtensoLocal(dateStr) {
  if (typeof globalUtils?.formatarDataExtenso === 'function') {
    try {
      return globalUtils.formatarDataExtenso(dateStr);
    } catch (err) {
      console.warn('Erro ao formatar data com globalUtils.formatarDataExtenso:', err);
    }
  }
  try {
  let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y,m,day] = dateStr.split('-').map(Number);
    d = new Date(y, m-1, day); // local
  } else {
    d = new Date(dateStr);
  }
    const fmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    return fmt.format(d);
  } catch (_) {
    return dateStr;
  }
}

const localDataTexto = computed(() => {
  const baseDate = dataCertificadoEfetiva.value;
  const data = formatarDataExtensoLocal(baseDate);

  const c = (municipioNome.value || '').trim();
  const uf = (estadoSigla.value || '').trim();

  if (c && uf) return `${c}/${uf}, ${data}`;
  if (c)       return `${c}, ${data}`;
  if (uf)      return `${uf}, ${data}`;
  return data;
});

const PREVIEW_FONT_FAMILY = 'Roboto, Helvetica, Arial, sans-serif';

function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.max(0, r || 0);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  // não stroke aqui; quem chama faz o stroke
}


function drawPreview() {
  const canvas = canvasPreview.value;
  if (!canvas) return;
  const cfg = layout.value;
  if (!cfg) return;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const parentW = canvas.parentElement?.clientWidth || img.width;
    const scale = Math.min(1, parentW / img.width);
    const viewW = Math.round(img.width * scale);
    const viewH = Math.round(img.height * scale);

    canvas.width = viewW;
    canvas.height = viewH;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, viewW, viewH);
    ctx.drawImage(img, 0, 0, viewW, viewH);

    // === Moldura colorida (usar parâmetros do layout e cor do nível) ===
    const bordaCfg = (layout.value?.borda || {});
    const bwPxImg   = ptToPx(Number(bordaCfg.larguraPt) || 18); // espessura em px (origem imagem)
    const mgPxImg   = ptToPx(Number(bordaCfg.margemPt)  || 28); // margem interna em px (origem imagem)
    const radPxImg  = ptToPx(Number(bordaCfg.raioPt)    || 0);  // raio em px (origem imagem)
    const bw = bwPxImg * scale;
    const mg = mgPxImg * scale;
    const rad = radPxImg * scale;
    const colorHex = (corBorda.value || corDefault.value);
    if (bw > 0) {
      ctx.save();
      ctx.lineWidth = bw;
      ctx.strokeStyle = colorHex;
      // stroke é centrado na linha => compensar metade da espessura
      const x = mg + bw / 2;
      const y = mg + bw / 2;
      const w = viewW - (mg * 2) - bw;
      const h = viewH - (mg * 2) - bw;
      roundRectPath(ctx, x, y, w, h, rad);
      ctx.stroke();
      ctx.restore();
    }

    const drawTextBox = (text, box = {}) => {
      const fontSize = (box.size || 36) * scale;
      const wantsBold = (box.font || '').toLowerCase().includes('bold');
      const weight = wantsBold ? '700' : '400';
      ctx.font = `${weight} ${fontSize}px ${PREVIEW_FONT_FAMILY}`;
      ctx.fillStyle = box.color || '#000';
      ctx.textBaseline = 'top';

      let t = String(text || '');
      if (box.uppercase) t = t.toUpperCase();

      const x = Math.round((box.x || 0) * scale);
      const y = Math.round((box.y || 0) * scale);
      const w = Math.round((box.w || img.width) * scale);

      const m = ctx.measureText(t);
      let tx = x;
      if (box.align === 'center') tx = x + Math.floor(w / 2) - m.width / 2;
      if (box.align === 'right')  tx = x + w - m.width;

      ctx.fillText(t, tx, y);
    };

    // Monta os textos finais
    const nome  = alunoNome.value;
    const nivel = nivelTexto.value;
    const dataTx = localDataTexto.value;

    drawTextBox(nome,  cfg.nome);
    drawTextBox(nivel, cfg.nivel);
    drawTextBox(dataTx, cfg.data);
  };
  img.src = certificadoImgUrl.value;
}

async function gerarPrevia() {
  try {
    isGeneratingPreview.value = true;
    await drawPreview();
  } finally {
    isGeneratingPreview.value = false;
  }
}

async function enviarCertificado() {
  if (isSending.value) return;
  isSending.value = true;
  erro.value = '';

  try {
    const payload = {
      empresa_id:        props.empresaId || 1,
      aluno_id:          props.aluno?.id,
      aluno_nome:        alunoNome.value,
      nivel_texto:       nivelTexto.value,
      nivel_id:          nivelId.value || undefined,     // se você já incluiu essa parte antes
      supervisao_id:     props.supervisaoId || undefined,
      data_emissao:      dataEmissao.value,      // YYYY-MM-DD
      responsavel_email: responsavelEmail.value,
      destinatario_nome: preview.value?.responsavel_nome || '',   // <<< AQUI
      por:               userName || undefined,
      assunto:   modalTitle.value,
      corpo_html: corpoHtml.value
};
    const { data } = await axios.post(`/configuracoes/certificados/enviar`, payload);
    const status = (data?.status_envio || '').toUpperCase();
    if (status === 'SENT') {
      emit('sent');
      close();
    } else {
      // mantém modal aberto e mostra motivo
      const motivo = data?.erro_envio || 'Falha no envio (status FAILED).';
      throw new Error(motivo);
    }
  } catch (e) {
    console.error('Erro enviarCertificado:', e);
    erro.value = e?.response?.data?.error || e?.message || 'Erro ao enviar certificado.';
  } finally {
    isSending.value = false;
  }
}

// ✅ validação do readOnly baseada no campo editável (responsavelEmail)
const emailValidoReadOnly = computed(() => {
  const e = (responsavelEmail.value || '').trim();
  return !!e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
});

// ✅ Reenviar usando o email editado (se o usuário corrigiu)
async function reenviarCertificado() {
  if (isSending.value) return;
  isSending.value = true;
  erro.value = '';

  try {
    const destinatarioEmail = (responsavelEmail.value || '').trim();
    const destinatarioNome =
      (preview.value?.responsavel_nome || sentEmail.value?.destinatario_nome || '').trim();

    if (!destinatarioEmail || !emailValidoReadOnly.value) {
      throw new Error('Informe um e-mail válido para reenviar.');
    }

    // Reenvio deve usar o conteúdo atualizado da supervisão.
    // O snapshot antigo fica apenas como fallback.
    if (!preview.value) {
      await loadPreview();
    }

    const htmlAtualizado = (corpoHtml.value || '').trim();

    if (!htmlAtualizado) {
      throw new Error('Não foi possível gerar o conteúdo atualizado para reenviar.');
    }

    const payload = {
      empresa_id:        props.empresaId || 1,
      aluno_id:          alunoIdEfetivo.value || props.aluno?.id || null,
      aluno_nome:        alunoNome.value,
      nivel_texto:       nivelTexto.value,
      nivel_id:          nivelId.value || undefined,
      supervisao_id:     props.supervisaoId || undefined,

      // backend exige (YYYY-MM-DD)
      data_emissao:      dataEmissao.value,

      // para onde vai
      responsavel_email: destinatarioEmail,
      destinatario_nome: destinatarioNome || undefined,

      // auditoria
      por:               userName || undefined,

      // no reenvio, usa assunto e corpo atualizados
      assunto:           (modalTitle.value || sentEmail.value?.assunto || '').trim(),

      corpo_html:        htmlAtualizado
    };

    const { data } = await axios.post(`/configuracoes/certificados/enviar`, payload);

    const status = String(data?.status_envio || '').toUpperCase();
    if (status !== 'SENT') {
      throw new Error(data?.erro_envio || 'Falha no reenvio.');
    }

    // atualiza rodapé "E-mail enviado em..." com o último envio
    await loadSentEmail();

    emit('sent');
  } catch (e) {
    console.error('Erro reenviarCertificado:', e);
    erro.value = e?.response?.data?.error || e?.message || 'Erro ao reenviar certificado.';
  } finally {
    isSending.value = false;
  }
}

function resolverNivelCertificado(data) {
  const direto =
    data?.nivel_certificado ||
    data?.nivel_concluido ||
    data?.nivel_origem ||
    data?.nivel_atual_nome ||
    data?.nivel_atual ||
    '';

  if (direto) return String(direto).trim();

  const formulario = String(data?.formulario_nome || '').trim();

  const match = formulario.match(
    /(?:do|da)\s+(nível\s+[IVXLCDM0-9]+)(?:\s+para|\s+ao)/i
  );

  if (match?.[1]) {
    return match[1].toUpperCase();
  }

  return '';
}

async function loadPreview() {
  if (!props.supervisaoId) {
    preview.value = null;
    previewError.value = 'supervisaoId ausente.';
    return;
  }

  previewLoading.value = true;
  previewError.value = null;
  preview.value = null;

  try {
    const params = {};
    if (props.aluno?.matricula_id) params.matriculaId = props.aluno.matricula_id;

    const { data } = await axios.get(
      `/operacional/troca-nivel/${props.supervisaoId}/preview`,
      { params }
    );

    preview.value = data || null;

    const nivelDaAvaliacao = resolverNivelCertificado(data);

    if (nivelDaAvaliacao) {
      nivelTexto.value = nivelDaAvaliacao;
    }

    if (data?.responsavel_email) {
      responsavelEmail.value = data.responsavel_email;
    }
  } catch (e) {
    console.error('Preview troca-nivel erro:', e);
    previewError.value = 'Falha ao carregar pré-visualização.';
  } finally {
    previewLoading.value = false;
  }
}

// Watch de abertura do modal
watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return;
    erro.value = '';
    sentEmail.value = null;
    sentHtml.value = '';
    isLoading.value = true;
    try {
      await carregarEmpresaELayout();
      await carregarLocalidadePorEmpresa();

      // Em readOnly, ainda pode carregar resumo (para mostrar “Resumo da Avaliação”)
      await carregarResumoSupervisao();

      if (props.readOnly) {
        await loadSentEmail();        // carrega auditoria/snapshot anterior
        await carregarNivelDoAluno(); // carrega nível/cor atual
        await loadPreview();          // carrega conteúdo atualizado da supervisão
      } else {
        await carregarNivelDoAluno();
        await loadPreview();
      }

      await document.fonts?.ready;
      await gerarPrevia();
    } catch (e) {
      erro.value = e?.message || 'Falha ao carregar dados do certificado.';
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

function close() {
  emit('close');
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 0;
  overflow: hidden;
}

.modal-header {
  background: #f5f5f5;
  padding: .75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
}

.modal-footer {
  background: #f5f5f5;
  padding: .75rem 1rem;
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  flex-shrink: 0;
}

.modal-body canvas {
  display: block;
}

.close-icon {
  font-size: 18px;
  cursor: pointer;
}
</style>